import _ from 'lodash';
import RequestContextService from '../RequestContextService.js';
import getBaseUrl from '../getBaseUrl.js';
import getCSRFCookie from './getCSRFCookie.js';
import getAuthTokenExpirationCookie from './getAuthTokenExpirationCookie.js';
import refreshMicrosoftIdentity from './refreshMicrosoftIdentity.js';

const REFRESH_LOCK_NAME = 'skyjs-refresh-auth-token';

// Every tab of the app shares the refresh token cookie, and the server ends the session when a
// replaced refresh token comes back after its grace period. Tabs therefore refresh one at a time:
// a tab that waited for the lock while another refreshed finds the auth token expiration cookie
// changed, and uses the cookies that tab received instead of rotating the refresh token again.
export default async function refreshAuthToken() {
  if (typeof navigator === 'undefined' || !navigator.locks) {
    return doRefreshAuthToken();
  }

  const expirationBeforeLock = getAuthTokenExpirationCookie();

  return navigator.locks.request(REFRESH_LOCK_NAME, async () => {
    const expiration = getAuthTokenExpirationCookie();

    if (expiration != null && expiration !== expirationBeforeLock) {
      return { authTokenExpirationDate: expiration };
    }

    return doRefreshAuthToken();
  });
}

async function doRefreshAuthToken() {
  const baseUrl = getBaseUrl();
  const csrf = getCSRFCookie();
  const requestContext = RequestContextService.get();

  const thirdPartyToken = await refreshMicrosoftIdentity();

  const response = await fetch(`${baseUrl}/authentication-refresh-token`, {
    headers: {
      Accept: 'application/json',
      'Accept-encoding': 'gzip',
      'Content-type': 'application/json; charset=UTF-8',
      ...(csrf && { 'X-CSRF-Token': csrf })
    },
    method: 'POST',
    keepalive: true,
    body: JSON.stringify(
      _.omitBy(
        {
          refreshToken: requestContext?.refreshToken,
          thirdPartyToken
        },
        _.isNil
      )
    )
  });

  if (!response.ok) {
    throw Object.assign(
      new Error(`Error while refreshing authToken : ${response.status} ${response.statusText}`),
      { status: response.status }
    );
  }

  const { authToken, authTokenExpirationDate, refreshToken } = await response.json();
  return { authToken, authTokenExpirationDate, refreshToken };
}
